<template>
  <AgGridVue
    class="ag-grid--custom ag-grid--insights ag-theme-balham-dark"
    :grid-options="gridOptions"
    :column-defs="gridColDefs"
    :column-types="colTypes"
    :default-col-def="defaultColDef"
    :auto-group-column-def="autoGroupColumnDef"
    :row-buffer="rowBuffer"
    :row-model-type="rowModelType"
    :cache-block-size="cacheBlockSize"
    :max-blocks-in-cache="maxBlocksInCache"
    :row-height="29.6"
    row-group-panel-show="never"
    :side-bar="sideBar"
    :animate-rows="false"
    :debug="debug"
    :get-row-node-id="getRowNodeId"
    :group-include-footer="true"
    :group-include-total-footer="true"
    :group-remove-lowest-single-children="true"
    :group-default-expanded="-1"
    :suppress-agg-func-in-header="true"
    :suppress-column-state-events="false"
    :suppress-column-move-animation="true"
    :suppress-make-column-visible-after-un-group="true"
    :suppress-property-names-check="true"
    :pivot-mode="false"
    :row-class-rules="rowClassRules"
    :get-main-menu-items="getMainMenuItems"
    :group-header-height="0"
    :framework-components="frameworkComponents"
    :loading-cell-renderer="loadingCellRenderer"
    :loading-cell-renderer-params="loadingCellRendererParams"
    :block-load-debounce-millis="1000"
    :suppress-context-menu="true"
    :icons="customIcons"
    :suppress-animation-frame="true"
    :tooltip-show-delay="0"
    @grid-ready="onGridReady"
    @rowGroupOpened="onRowGroupOpened"
    @filterChanged="onFilterChange"
    @sortChanged="onSortChange"
    @columnPivotModeChanged="onPivotModeChange"
    @columnPivotChanged="onPivotChange"
    @columnVisible="onColVisChange"
    @columnMoved="onColumnMoved"
    @columnPinned="onColPinChange"
    @columnResized="onColResizeChange"
    @columnValueChanged="onColumnValueChanged"
    @cellClicked="e => $emit('cellClicked', e)"
    @toolPanelVisibleChanged="onToolPanelVisibleChanged"
  />
</template>

<script>
import { isEmpty, differenceWith, debounce } from 'lodash';
import { AgGridVue } from '@ag-grid-community/vue';

import analyticsMixinFactory from '@/core/mixins/analyticsMixinFactory';
import { convertColumnsToValueObjects } from '@/modules/reporting/utils';
import RingbaLoadingCellRenderer from './ringbaLoadingCellRenderer';
import { agGridService } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import QuickInsights from '@/modules/reporting/components/ReportingInsights/QuickInsights';

import { 
  columnTypes, 
  numericTypes, 
  handleDocumentClickListenerToCloseToolPanel,
  isWhitespace
} from '@/modules/reporting/components/AgGridUtils';
import cloneDeep from 'lodash/cloneDeep';

const QUICK_INSIGHTS_ICON = 'quick-insights-icon';
const INSIGHTS_GRID_ANALYTICS_CATEGORY = 'insights-grid';
const AGGREGATE_COLUMN_SHOW_ACTION = 'aggregate-column-show';
const AGGREGATE_COLUMN_HIDE_ACTION = 'aggregate-column-hide';
const COLUMN_MOVED_ACTION = 'column-moved';
const COLUMN_SORT_ACTION = 'column-sorted';
const ROW_GROUP_EXPAND_ACTION = 'row-group-expand';
const ROW_GROUP_COLLAPSE_ACTION = 'row-group-collapse';
const TOOL_PANEL_TOGGLE_ACTION = 'toolpanel-toggle';

function pinTotalsRowToBottom({totalsData, gridApi}) {
  let totalsRow = {};

  for (const valueCol in totalsData) {
    totalsRow[valueCol] = totalsData[valueCol];
  }

  gridApi.setPinnedBottomRowData([totalsRow]);
}

function hideQuickInsightsSideButton() {
  let quickInsightsIconElement = document.querySelector(`.${QUICK_INSIGHTS_ICON}`);

  if (quickInsightsIconElement) {
    let parentButton = quickInsightsIconElement.closest('.ag-side-button');

    if (parentButton) {
      parentButton.style.display = 'none';
    }
  }
}

// Avoid change tracking overhead
let gridApi = false;
let gridColumnApi = false;
let handleDocumentClick;


export default {
  name: 'AgGridWrapper',
  components: {
    AgGridVue
  },
  mixins: [
    analyticsMixinFactory(INSIGHTS_GRID_ANALYTICS_CATEGORY)
  ],
  props: {
    /** 
     * The field name by which you want to group by
     * This is the top-level grouping (eg. In insights, the tabs define the top-level grouping)
     */
    groupBy: {
      type: String,
      default: ''
    },
    groupByHeirarchy: {
      type: Array,
      default() {
        return[];
      } 
    },
    defaultColDefs: {
      type: Array,
      default() {
        return null;
      } 
    },
    /**
     * Column definitations for Ag-grid
     * Don't change column state from colDefs
     */
    colDefLookUp: {
      type: Object,
      default() {
        return {};
      }
    },
    /**
     * The grid state
     */
    gridState: {
      type: Object,
      default() {
        return {};
      }
    },
    /**
     * Property to watch over and trigger the grid to export
     */
    exportData: {
      type: Object,
      default() {
        return {
          shouldExport: false,
          params: {
            allColumns: false
          }
        };
      }
    },
    lightTheme: {
      type: Boolean,
      default: true
    },
    refreshData: {
      type: Number,
      default: 0
    },
    openColumnsToolPanel: {
      type: Boolean,
      default: false
    },
    initialExpandAllGroups: {
      type: Boolean,
      default: false
    },
    toggleQuickInsightsPanel: {
      type: Boolean,
      default: false
    },
    colDefsLoadedFromApi: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      autoGroupColumnDef: null,
      defaultColDef: null,
      colTypes: {},
      gridOptions: null,
      rowBuffer: null,
      cacheBlockSize: 500,
      maxBlocksInCache: 10,
      sideBar: null,
      debug: false,
      rowModelType: null,
      gridExpandedGroupIds: new Set(),
      gridStateId: performance.now(),
      lastGroupByHeirarchy: [...this.gridState.rowGroupCols],
      gridColDefs: null,
      settingGridState: false, // Flag to indicate the grid state has been changed manually, so we don't change the Vuex grid state unnecessarily
      rowClassRules: {
        'ag-grid--disable-expand': (params) => {
          return params.node.leafGroup;
        }
      },
      frameworkComponents: null,
      loadingCellRenderer: null,
      loadingCellRendererParams: null,
      customIcons: Object.freeze({
        'quick-insights': `<i class="${QUICK_INSIGHTS_ICON}"></i>`
      })
    };
  },
  computed: {
    expandedGroupIds() {
      return (this.gridState && this.gridState.expandedGroupIds) || [];
    },
    gridColLookUp() {
      const lookup = {};
      if (this.gridColDefs && this.gridColDefs.length) {
        this.gridColDefs.forEach(c => {
          if(c.children && c.children.length) {
            c.children.forEach(cc => {
              if(cc.colId) {
                lookup[cc.colId] = true;
              }
            });
          }
          if(c.colId) {
            lookup[c.colId] = true;
          }
        });
      }
      return lookup;
    }
  },
  watch: {
    refreshData(newValue, oldValue) {
      if (newValue !== oldValue && gridApi) {
        gridApi.purgeServerSideCache([]);
      }
    },
    gridState: {
      immediate: true,
      handler(newValue) {
        if (!isEmpty(newValue) && newValue.id !== this.gridStateId) {
          this.gridStateId = newValue.id || performance.now();
          this.gridExpandedGroupIds = new Set(newValue.expandedGroupIds);
          this.setGridState(newValue);
        }
      }
    },
    exportData(newValue) {
      if (newValue.shouldExport) {
        this.exportExcel(newValue.params);
      }
    },
    groupByHeirarchy(newValue) {
      if (newValue && newValue.length && !this.rowGroupsEqual(newValue, this.lastGroupByHeirarchy) && gridColumnApi) {
        this.lastGroupByHeirarchy = [...newValue];
        this.updateRowGroupCols(newValue);
      }
    },
    colDefLookUp() {
      this.updateRowGroupCols(this.groupByHeirarchy);
    },
    openColumnsToolPanel(newValue) {
      if (newValue) {
        gridApi.openToolPanel('columns');
      }
    },
    toggleQuickInsightsPanel() {
      if (gridApi.isToolPanelShowing() && gridApi.getOpenedToolPanel() === 'quickInsights') {
        gridApi.closeToolPanel('quickInsights');
      } else if ((gridApi.isToolPanelShowing() && gridApi.getOpenedToolPanel() === 'columns') || !gridApi.isToolPanelShowing()) {
        gridApi.openToolPanel('quickInsights');
      }
    },
    defaultColDefs(newValue, oldValue) {
      if (newValue.length && oldValue.length 
        && newValue[0].children && oldValue[0].children
        && newValue[0].children.length && oldValue[0].children.length) {
        var different = false;
        for (let i = 0; i < newValue[0].children.length; i++) {
          different = different || newValue[0].children[i]?.children?.length !== oldValue[0].children[i]?.children?.length;
        }
        if (different) {
          this.gridColDefs = this.defaultColDefs;

          this.$nextTick(() => {
            this.setGridState(this.gridState);
          });
        } 
      }
    },
  },
  beforeMount() {
    this.gridOptions = {};

    this.setColumnDefinitions();
    this.rowBuffer = 20;
    this.cacheBlockSize = 500;
    this.maxBlocksInCache = 10;
    this.rowModelType = 'serverSide';
    this.setSidebar();
    
    this.colTypes = {
      ...columnTypes
    };

    this.frameworkComponents = { 
      customLoadingCellRenderer: RingbaLoadingCellRenderer,
      quickInsightsToolPanel: QuickInsights 
    };
      
    this.loadingCellRenderer = 'customLoadingCellRenderer';
    this.loadingCellRendererParams = { loadingMessage: 'Loading Insight data...' };
  },
  destroyed () {
    gridApi = false;
    gridColumnApi = false;

    document.removeEventListener('click', handleDocumentClick, true);

    handleDocumentClick = null;
  },
  methods: {
    rowGroupsEqual(a, b){ 
      if(a.length !== b.length) {
        return false;
      }
      for (let i = 0; i < a.length; i++) {
        if(a[i].id !== b[i].id) {
          return false;
        }
      }
      return true;
    },
    updateRowGroupCols(rowGroupCols) {
      if (gridColumnApi) {
        const rowGroupColKeys = rowGroupCols.map(col => col.id);
        const colsToAdd = [];
        let colNotFoundInColDef = false;
        const tempGridState = cloneDeep(this.gridState);

        rowGroupColKeys.forEach(id => {
          if (!this.colDefLookUp[id]) {
            colNotFoundInColDef = colNotFoundInColDef || true;
          } else if (!this.gridColLookUp[id]) {
            colsToAdd.push(this.colDefLookUp[id]);
          }
        });

        // Don't set row group columns if colDef is not defined
        if (colNotFoundInColDef) {
          return;
        }

        if(colsToAdd.length && gridApi) {
          this.gridColDefs = this.gridColDefs?.concat(colsToAdd) || colsToAdd;
          gridApi.setColumnDefs(this.gridColDefs);
        }

        // Approach 1: Set updated rowGroup in column state
        // The columnDefs gets the default column definitions we've hardcoded, the column state in that definition will not be what the user saved in the saved insight
        // That's the reason why we've to call setGridState after setting the new rowGroupColumns
        // This behaviour changed after we upgraded to version 24 of AgGrid
        // Things we do here:
        // 1 - Set the grid state with the updated row group states of each column
        // 2 - Set the new column state        
        // Resolves this issue: https://app.shortcut.com/ringba/story/7217/fix-issue-when-trying-to-save-a-report-with-is-duplicate-breakdown
        setTimeout(() => {
          tempGridState.columnState.forEach((col) => {
            if (col.rowGroup) {
              const foundInColsToAddIdx = rowGroupCols.findIndex(colRowGroup => colRowGroup.id === col.colId);


              if (foundInColsToAddIdx === -1) {
                col.rowGroup = false;
                delete col.rowGroupIndex;
              } else {
                col.rowGroupIndex = foundInColsToAddIdx;
              }
            }
          });

          let colsToAddToState = [];

          rowGroupCols.forEach((col, idx) => {
            const foundCol = tempGridState.columnState.find(colState => col.id === colState.colId);

            if (!foundCol) {
              colsToAddToState.push({
                ...col,
                colId: col.id,
                rowGroup: true,
                rowGroupIndex: idx
              })
            } else {
              foundCol.rowGroup = true;
              foundCol.rowGroupIndex = idx;
            }
          });

          tempGridState.columnState = [
            ...tempGridState.columnState,
            ...colsToAddToState
          ];

          this.setGridState(tempGridState, true);
        }, 0);

        gridColumnApi.setRowGroupColumns(rowGroupColKeys);
      }
    },
    getMainMenuItems({ column, columnApi, defaultItems }) {
      defaultItems.splice(defaultItems.indexOf('resetColumns'), 1);
      
      return [
        {
          name: 'Hide column',
          action() {
            columnApi.removeValueColumn(column.colId);
          },
          icon: '<span class="ag-icon ag-icon-tree-indeterminate"></span>',
          disabled: column.colDef.ringbaAutoColumn
        },
        ...defaultItems,
      ];
    },
    onColumnMoved: debounce(function onColumnMoved(input) {
      this.onGridStateChange(input);
      this.trackEvent(COLUMN_MOVED_ACTION, input?.columns?.map(col => col.colId).toString());
    }, 1000),
    onFilterChange: debounce(function stateChange(input) {
      this.onGridStateChange(input);
    }, 100),
    onSortChange: debounce(function stateChange(input) {
      this.onGridStateChange(input);
      this.trackEvent(COLUMN_SORT_ACTION, input?.columns?.map(col => col.colId).toString());
    }, 100),
    onPivotModeChange: debounce(function stateChange(input) {
      this.onGridStateChange(input);
    }, 100),
    onPivotChange: debounce(function stateChange(input) {
      this.onGridStateChange(input);
    }, 100),
    onColVisChange(input) {
      if (input.source === 'columnMenu' || input.source === 'toolPanelUi') {
        let colKeys = input.columns.map(col => col.colId);
        let areColsVisible = input.columns.some(col => col.visible);

        if (areColsVisible) {
          gridColumnApi.addValueColumns(colKeys);
          this.trackEvent(AGGREGATE_COLUMN_SHOW_ACTION, colKeys?.toString());
        } else {
          gridColumnApi.removeValueColumns(colKeys);
          this.trackEvent(AGGREGATE_COLUMN_HIDE_ACTION, colKeys?.toString());
        }
      }
      this.onGridStateChange(input);
    },
    onColPinChange: debounce(function stateChange(input) {
      this.onGridStateChange(input);
    }, 100),
    onColResizeChange: debounce(function stateChange(input) {
      this.onGridStateChange(input);
    }, 100),
    exportExcel(params) {
      gridApi.exportDataAsExcel(Object.assign({}, params, { sheetName: 'Insights' }));
    },
    onGridReady(params) {
      gridApi = params.api;
      gridColumnApi = params.columnApi;

      agGridService.setGridCallback(this.restoreExpandedGroups);

      this.setGridState(this.gridState);

      params.api.setServerSideDatasource(agGridService);
      params.api.hideOverlay();

      this.initQuickInsightsPanelOutsideListener();

      hideQuickInsightsSideButton();
    },
    initQuickInsightsPanelOutsideListener() {
      let outsideClickListener = handleDocumentClickListenerToCloseToolPanel(gridApi);

      handleDocumentClick = function (ev) {
        outsideClickListener(ev, document.querySelector('.quick-insights-btn'));
      };

      document.addEventListener('click', handleDocumentClick, true);
    },
    restoreExpandedGroups(resultForGrid, isTopLevel, totalRows, partialResult, allRecords) {
      if (partialResult) {
        this.$emit('showPartialDataNotification');
      } else {
        this.$emit('hidePartialDataNotification');
      }

      if (isTopLevel && totalRows) {
        this.$emit('onGridTopLevelDataLoaded', { data: allRecords });
        pinTotalsRowToBottom({ totalsData: totalRows[0], gridApi });
      }

      resultForGrid.forEach((row) => {
        if (this.initialExpandAllGroups || this.gridExpandedGroupIds.has(row.agGridHash)) {
          const node = gridApi.getRowNode(row.agGridHash);

          if((this.initialExpandAllGroups || node?.setExpanded) && !node.leafGroup) {
            node.setExpanded(true);
          }
        }
      });
    },
    onRowGroupOpened({ node }) {
      if (node.expanded) {
        this.gridExpandedGroupIds.add(node.id);
        this.trackEvent(ROW_GROUP_EXPAND_ACTION, `${node.field}-${node.level}`);
      } else {
        this.gridExpandedGroupIds.delete(node.id);
        this.trackEvent(ROW_GROUP_COLLAPSE_ACTION, `${node.field}-${node.level}`);
      }

      this.onGridStateChange();
    },

    getRowNodeId(item) {
      if (item.agGridHash) {
        return item.agGridHash;
      }

      return performance.now();
    },

    setColumnDefinitions() {
      this.gridColDefs = this.defaultColDefs;

      this.defaultColDef = {
        width: 115,
        sortable: true,
        resizable: true,
        editable: false,
        menuTabs: ['generalMenuTab'],
        cellClass: this.getCellClass,
        pinnedRowCellRenderer(params) {
          return `<div class="ag-grid-pinned-row">${params.valueFormatted}</div>`;
        },
        toolPanelClass(params) {
          const classes = ['ag-grid-tool-panel-column-indicator'];

          if (params.colDef.enableRowGroup) {
            classes.push('ag-grid-tool-panel-column-indicator--group');
          }

          if (params.colDef.enableValue) {
            classes.push('ag-grid-tool-panel-column-indicator--value');
          }
          return classes;
        }
      };

      this.autoGroupColumnDef = {
        width: 250,
        suppressSizeToFit: true,
        pinned: true,
        lockVisible: true,
        headerName: 'Breakdown',
        ringbaAutoColumn: true,
        cellClass: function getGroupCellClass(params) {
          const classes = [];

          const colDef = params?.node?.rowGroupColumn?.colDef;
          if (colDef?.enableFilter || colDef?.type === 'tag') {
            classes.push('click-2-filter', 'ag-group-click-2-filter');
          }

          if (isWhitespace(params.value)) {
            classes.push('ag-grid--blanks');
          }

          return classes;
        },
        pinnedRowCellRenderer() {
          return `<div class="ag-grid-pinned-row ag-grid-pinned-row--totals">Totals</div>`;
        }
      };
    },
    getCellClass(params) { 
      if (params) {
        const classes = [];
        

        if (params.colDef) {
          if (numericTypes[params.colDef.type]) {
            classes.push('ag-right-aligned-cell');
          }

          if (params.colDef.colId !== 'callCount' 
          && params.colDef.allowedAggFuncs
          && params.colDef.allowedAggFuncs.length === 1
          && params.colDef.allowedAggFuncs[0] === 'count') {
            classes.push('click-2-filter');
          }
        }
        return classes;
      }
    },

    setSidebar() {
      this.sideBar = {
        toolPanels: [
          {
            id: 'columns',
            labelDefault: 'Columns',
            labelKey: 'columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
              suppressSyncLayoutWithGrid: true,
              suppressColumnSelectAll: true,
              contractColumnSelection: false,
              suppressRowGroups: true,
              suppressPivots: true,
              suppressPivotMode: true,
              suppressValues: true,
            }
          }
        ]
      };

      this.sideBar.toolPanels.push({
        id: 'quickInsights',
        labelDefault: '',
        labelKey: '',
        iconKey: 'quick-insights',
        toolPanel: 'quickInsightsToolPanel'
      });
    },
    getGridState(id) {
      return {
        rowGroupCols: [...convertColumnsToValueObjects(gridColumnApi?.getRowGroupColumns())],
        valueCols: [...convertColumnsToValueObjects(gridColumnApi?.getValueColumns())],
        pivotCols: [...convertColumnsToValueObjects(gridColumnApi?.getPivotColumns())],
        pivotMode: gridColumnApi?.isPivotMode(),
        expandedGroupIds: [...this.gridExpandedGroupIds],
        filterModel: gridApi?.getFilterModel(),
        columnState: gridColumnApi?.getColumnState(),
        id
      };
    },
    onColumnValueChanged() {
      if (!this.gridState || !this.gridState.valueCols || !gridColumnApi || !gridApi) {
        return;
      }

      let valueCols = [];
      let gridValueColumns = gridColumnApi.getValueColumns();
      
      if (this.gridState.valueCols.length !== gridValueColumns.length) {
        if (this.gridState.valueCols.length > gridValueColumns.length) {
          valueCols = differenceWith(this.gridState.valueCols, gridValueColumns, (firstArr, secondArr) => {
            return firstArr.id === secondArr.colId;
          });
          gridColumnApi.setColumnsVisible(valueCols.map(col => col.id), false);
        } else {
          valueCols = differenceWith(gridValueColumns, this.gridState.valueCols, (firstArr, secondArr) => {
            return firstArr.colId === secondArr.id;
          });
          gridColumnApi.setColumnsVisible(valueCols.map(col => col.colId), true);
        }
      }

      this.onGridStateChange();
    },
    onGridStateChange() {
      if (this.colDefsLoadedFromApi) {
        this.gridStateId = performance.now();
        this.$emit('gridStateChange', this.getGridState(this.gridStateId));
      }
    },
    setGridState(gridState, avoidGroupCols) {
      if (isEmpty(gridState) || !gridApi || !gridColumnApi || !this.colDefsLoadedFromApi) {
        return;
      }

      if (gridState.rowGroupCols && !avoidGroupCols) {
        this.updateRowGroupCols(gridState.rowGroupCols);
      }

      if (gridState.valueCols) {
        gridColumnApi.addValueColumns(gridState.valueCols.map(col => col.id));
      }

      if (gridState.filterModel) {
        gridApi.setFilterModel(gridState.filterModel);
      }

      if (gridState.columnState) {
        gridColumnApi.applyColumnState({ 
          state: gridState.columnState,
          applyOrder: true,
          defaultState: {
            hide: true,
          }
        });
      }

    },

    onToolPanelVisibleChanged(evt) {
      this.trackEvent(TOOL_PANEL_TOGGLE_ACTION, `${evt.source}-${gridApi.isToolPanelShowing()}`);
    }
  }
};

</script>

<style lang="scss">
.ag-grid--insights {
  height: 100%;

  .ag-header-row:nth-child(1) {
    display: none;
  }

  .ag-pinned-left-cols-container {
    .ag-row-level-0, .ag-row-level-7 {
      border-left: var(--component-table__row--group-level-0) solid 3px;
    }
    .ag-row-level-1, .ag-row-level-8 {
      border-left: var(--component-table__row--group-level-1) solid 3px;
    }
    .ag-row-level-2, .ag-row-level-9 {
      border-left: var(--component-table__row--group-level-2) solid 3px;
    }
    .ag-row-level-3, .ag-row-level-10 {
      border-left: var(--component-table__row--group-level-3) solid 3px;
    }
    .ag-row-level-4, .ag-row-level-11 {
      border-left: var(--component-table__row--group-level-4) solid 3px;
    }
    .ag-row-level-5, .ag-row-level-12 {
      border-left: var(--component-table__row--group-level-5) solid 3px;
    }
    .ag-row-level-6, .ag-row-level-13 {
      border-left: var(--component-table__row--group-level-6) solid 3px;
    }
  }

  .ag-body-horizontal-scroll {
    background-color: var(--component-sidebar__submenu-bg);

    .ag-body-horizontal-scroll-viewport {
      &::-webkit-scrollbar {
        height: 8px;
      }
      &::-webkit-scrollbar-track, &::-webkit-scrollbar-corner {
        background-color: var(--component-sidebar__submenu-bg);
      }

      &::-webkit-scrollbar-thumb {
        background-color: var(--component-grid-scroll-thumb-color);
      }

      scrollbar-color: var(--component-grid-scroll-thumb-color) var(--component-sidebar__submenu-bg);
      scrollbar-width: thin;
    }
  }
}

.ag-grid--custom {
  color: var(--reset-body-font-color);

  // Pinned columns - Start
  .ag-pinned-left-header {
    border-right-color: var(--ag-header-column-separator-color, rgba(26, 29, 41, 0.5));
  }

  .ag-pinned-right-header {
    border-left-color: var(--ag-header-column-separator-color, rgba(26, 29, 41, 0.5));
  }
  // Pinned columns - End

  .ag-overlay-loading-center {
    color: var(--reset-body-font-color);
  }

  .ag-header-cell.ag-header-cell {
    &:not(.ag-header-group-cell-no-group) {
      border: none;
    }

    &::after {
      height: 26px;
      top: calc(35% - 25%);
    }
  }

  .ag-column-select-column-drag-handle, .ag-column-select-column-group-drag-handle {
    display: none;
  }

  .ag-pinned-left-cols-container, .ag-pinned-right-cols-container {
    z-index: 1;
    box-shadow: 0px -14px 40px -2px rgba(0, 0, 0, 0.3), 0 -14px 8px 0 rgba(36, 54, 65, 0.3), 0 -14px 8px 1px rgba(36, 54, 65, 0.3);
  }

  &.ag-theme-balham-dark {
    background: var(--component-ag-grid__balham-theme-background-color);
    
    .ag-root,
    .ag-header,
    .ag-paging-panel,
    .ag-header-group-cell {
      border-color: var(--component-table__border-color);
    }

    .ag-column-panel-column-select {
      border-bottom: none;
      border-top: none;
    }

    .ag-row-selected {
      background: var(--component-ag-grid-row--selected);
    }

    .ag-side-bar {
      &.ag-side-bar-right {
        border: none;
      }

      .ag-tool-panel-wrapper {
        border-left: 1px solid;
        border-color: var(--component-table__border-color) !important;
      }
    }
  }


  .ag-column-select-panel {
    .ag-column-tool-panel-column {
      &.ag-toolpanel-indent-1 {
        padding-left: 30px;
      } 

      &.ag-toolpanel-indent-2 {
        padding-left: 30px;
      }
    }
  }

  .ag-horizontal-left-spacer, .ag-horizontal-right-spacer {
    overflow-x: hidden;
  }

  .ag-body-horizontal-scroll .ag-body-horizontal-scroll-viewport {
    &::-webkit-scrollbar {
      height: 8px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: var(--component-grid-scroll-thumb-color);
    }

    scrollbar-color: var(--component-grid-scroll-thumb-color);
    scrollbar-width: thin;
  }

  .ag-tool-panel-wrapper {
    width: 380px;
  }

  .ag-menu, .ag-popup-editor, .ag-popup, .ag-tab-body {
    background: var(--component-table__header-row-bg);
    color: var(--reset-body-font-color);
  }

  .ag-tab-header {
    background-color: var(--component-tab-nav__link--active-bg);

    .ag-tab.ag-tab-selected {
      background-color: var(--component-table__header-row-bg);
    }
  }

  .ag-body-viewport, .ag-primary-cols-list-panel, .ag-column-drop-list, .ag-center-cols-viewport {
    &::-webkit-scrollbar {
      width: 17px;
      height: 0;
    }

    &::-webkit-scrollbar-track, &::-webkit-scrollbar-corner {
      background-color: var(--component-tab-nav__content-bg-color);
    }

    &::-webkit-scrollbar-thumb {
      background-color: var(--component-grid-scroll-thumb-color);
      border-left: 4.5px solid var(--component-tab-nav__content-bg-color);
      border-right: 4.5px solid var(--component-tab-nav__content-bg-color);
    }

    // These are supported only on Firefox as of Jan 15 '20
    scrollbar-color: var(--component-grid-scroll-thumb-color) var(--component-tab-nav__content-bg-color);
    scrollbar-width: thin; 
  }
  
  .ag-horizontal-left-spacer,
  .ag-ltr .ag-root:not(.ag-has-focus) .ag-cell.ag-cell-last-left-pinned:not(.ag-cell-range-right),
  .ag-ltr .ag-root:not(.ag-has-focus) .ag-cell.ag-cell-first-right-pinned:not(.ag-cell-range-left),
  .ag-ltr .ag-has-focus .ag-cell:not(.ag-cell-range-selected),
  .ag-ltr .ag-cell:not(.ag-cell-focus).ag-cell-first-right-pinned:not(.ag-cell-range-left) {
    border-color: transparent;
  }

  .ag-ltr .ag-cell:not(.ag-cell-focus).ag-cell-last-left-pinned:not(.ag-cell-range-right), .ag-ltr .ag-cell.ag-cell-range-selected:not(.ag-cell-range-single-cell).ag-cell-last-left-pinned:not(.ag-cell-range-right), .ag-ltr .ag-root:not(.ag-has-focus) .ag-cell.ag-cell-last-left-pinned:not(.ag-cell-range-right)
  .ag-ltr .ag-cell:not(.ag-cell-focus).ag-cell-first-right-pinned:not(.ag-cell-range-left), .ag-ltr .ag-cell.ag-cell-range-selected:not(.ag-cell-range-single-cell).ag-cell-first-right-pinned:not(.ag-cell-range-left), .ag-ltr .ag-root:not(.ag-has-focus) .ag-cell.ag-cell-first-right-pinned:not(.ag-cell-range-left) {
    border-color: transparent;
  }

  .ag-root.ag-layout-normal {
    height: auto;
  }

  .ag-column-tool-panel-column-label {
    pointer-events: none;
  }

  .ag-header {
    color: var(--reset-body-font-color);
    background-color: $table-head-bg;
  }

  .ag-row-even { // first row is row 0 is EVEN
    background-color: $table-body-bg-odd;
  }

  .ag-row-odd {
    background-color: $table-body-bg-even;
  }

  .ag-row.ag-row-last {
    border-bottom: 1px solid var(--component-table__border-color);
    border-bottom-width: 1px;
  }

  .ag-icon, .ag-column-drop-cell-text {
    color: var(--reset-body-font-color);
  }

  .ag-input-wrapper input[type="text"] {
    padding: 4px;
    border-color: var(--component-form__input-bg-default);
  }

  .ag-side-bar {
    .ag-side-buttons {
      background: var(--component-tab-nav__content-bg-color);

      .ag-side-button button {
        color: var(--reset-body-font-color);
      }

      .ag-selected button {
        background-color: var(--component-btn--secondary-bg-color);
        color: var(--white);

        .ag-icon {
          color: var(--white);
        }
      }
    }
  }

  input, select {
    background: transparent;
    color: var(--reset-body-font-color);
  }

  .ag-icon-checkbox-checked, .ag-icon-checkbox-indeterminate, .ag-icon-checkbox-unchecked {
    background: transparent;
  }

  .ag-column-drop-cell {
    background: var(--component-sidebar__submenu-bg);
    color: var(--component-ui-select__btn--default-font-color);
    .ag-icon.ag-icon-cancel {
      cursor: pointer;
    }
  }

  .ag-grid--disable-expand {
    .ag-group-contracted {
      display: none !important;
    }

    .ag-group-value {
      margin-left: 25px;
    }

    &.ag-row-level-0 {
      .ag-group-value {
        margin-left: 5px;
      }
    }
  }


  .ag-floating-bottom, .ag-row-pinned {
    overflow-y: hidden !important;
    border-top-color: var(--component-table__row--group-level-0);
    background-color: var(--component-sidebar__submenu-bg);
  }

  .ag-body-viewport {
    &.ag-layout-normal {
      overscroll-behavior: auto;
    }
  }

  .ag-group-contracted, .ag-group-expanded, .click-2-filter {
    cursor: pointer;
  }

  .click-2-filter:not(.ag-group-click-2-filter):hover, .click-2-filter .ag-group-value:hover {
    text-decoration: underline;
  }

  .ag-primary-cols-list-panel .ag-grid-tool-panel-column-hide-available {
    display: none;
  }

  .ag-menu-option {
    cursor: pointer;
  }

  .ag-status-bar {
    border: none;
  }
}

.ag-grid-pinned-row--totals {
  text-align: right;
}

.ag-grid-pinned-row {
  font-weight: 700;
}

.ag-column-tool-panel-column, .ag-column-tool-panel-column-group {
  cursor: default !important;

  .ag-column-group-icons {
    cursor: pointer;
  }

}

.ag-grid-tool-panel-column-indicator {
  position: relative;

  &::after {
    position: absolute;
    right: 10px;
    font-family: "agGridBalham";
    font-size: 1.3em;
    display: block;
  }
}

.ag-grid-tool-panel-column-indicator--group {
  display: none;
}

.ag-grid-tool-panel-column-indicator--value {
  cursor: default !important;
}

.ag-dnd-ghost {
  &.ag-theme-balham-dark {
    background: var(--component-ag-grid__header-cell--moving);
    color: var(--reset-body-font-color);

    .ag-icon {
      color: var(--reset-body-font-color);
    }
  }
}

.ag-grid--blanks {
  color: var(--orange);
}

</style>
